import React, { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import {
    PlayButtonSC,
    PlayerContainerSC,
    TimeDisplaySC,
    WaveformContainerSC,
    WaveTimeWrapperSC,
} from "./styled";
import { PlayIcon } from "../../../../../../assests/icons/PlayIcon";

interface AudioPlayerProps {
    audioUrl: string;
    waveColor?: string;
    progressColor?: string;
    height?: number;
}

const Index: React.FC<AudioPlayerProps> = ({
    audioUrl,
    waveColor = "#d1d5db",
    progressColor = "#8b9dc3",
    height = 17,
}) => {
    const waveformRef = useRef<HTMLDivElement>(null);
    const wavesurfer = useRef<WaveSurfer | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState("00:00");
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        if (!waveformRef.current) return;
        if (wavesurfer.current) return;

        // Create WaveSurfer instance
        wavesurfer.current = WaveSurfer.create({
            container: waveformRef.current,
            waveColor: waveColor,
            progressColor: progressColor,
            cursorColor: "transparent",
            barWidth: 3,
            barGap: 3,
            barRadius: 2,
            height: height,
            normalize: true,
            backend: "WebAudio",
            mediaControls: false,
        });

        // Load audio
        wavesurfer.current.load(audioUrl);

        // Event listeners
        wavesurfer.current.on("ready", () => {
            setIsReady(true);
        });

        wavesurfer.current.on("play", () => {
            setIsPlaying(true);
        });

        wavesurfer.current.on("pause", () => {
            setIsPlaying(false);
        });

        wavesurfer.current.on("finish", () => {
            setIsPlaying(false);
        });

        wavesurfer.current.on("audioprocess", () => {
            if (wavesurfer.current) {
                const time = wavesurfer.current.getCurrentTime();
                setCurrentTime(formatTime(time));
            }
        });

        // Cleanup
        return () => {
            if (wavesurfer.current && waveformRef.current && isReady) {
                wavesurfer.current.destroy();
            }
        };
    }, [audioUrl, waveColor, progressColor, height]);

    const formatTime = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };

    const togglePlay = () => {
        if (wavesurfer.current && waveformRef.current) {
            wavesurfer.current.playPause();
        }
    };

    return (
        <PlayerContainerSC>
            <PlayButtonSC
                $isPlaying={isPlaying}
                onClick={togglePlay}
                disabled={!isReady}
            >
                {isPlaying ? (
                    <svg viewBox="0 0 24 24">
                        <rect x="6" y="4" width="4" height="16" />
                        <rect x="14" y="4" width="4" height="16" />
                    </svg>
                ) : (
                    <PlayIcon />
                )}
            </PlayButtonSC>

            <WaveTimeWrapperSC>
                <WaveformContainerSC ref={waveformRef} />
                <TimeDisplaySC>{currentTime}</TimeDisplaySC>
            </WaveTimeWrapperSC>
        </PlayerContainerSC>
    );
};

export default Index;
